import { Flex, Group, Stack } from '@mantine/core';
import {
	useApiGetAiChatMetrics,
	useApiGetAiChatTimeseries,
	useApiGetAiImpactMetrics,
	useApiGetAiTimeSavingsTimeseries,
} from '@repo/api-codegen';
import { trackEvent } from '@repo/common/analytics';
import {
	Selector,
	SelectorTarget,
	SelectType,
} from '@repo/common/components/Selectors';
import { keepPreviousData } from '@tanstack/react-query';
import { observer } from 'mobx-react-lite';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useAuthUser } from '../../api';
import { AiQualityBadgeController } from '../AiQualityBadge/AiQualityBadgeController';
import { useOverviewMetricDatetime } from '../Overview/hooks/useMetricOverviewDatetime';
import { isValidTimeRange } from '../Overview/types';
import { AiChatList } from './components/AiChatList';
import { AiChatOverviewChart } from './components/AiChatOverviewChart';
import { AiChatRatedMessages } from './components/AiChatRatedMessages';
import { AiImpactDashboardSkeleton } from './components/AiImpactDashboardSkeleton';
import { AiMetricsCards } from './components/AiMetricsCards';
import { AiPromptsByMemberList } from './components/AiPromptsByMemberList';
import { AiTimeSavingsOverviewChart } from './components/AiTimeSavingsOverviewChart';
import { DATE_FILTER_OPTIONS } from './constants';
import {
	useInfiniteAiChatList,
	useInfiniteAiChatRatedMessages,
	useInfiniteAiPromptsByMember,
} from './hooks';
import type {
	AiChatRatedMessagesSortOrder,
	AiChatSortOrder,
	AiPromptsByMemberSortOrder,
	TimeRangeWithAll,
} from './types';
import {
	navigateToAiChats,
	navigateToAiChatsWithDateFilter,
	navigateToAiChatsWithTimeRange,
} from './utils/navigationUtils';

export const AiImpactDashboardController = observer(() => {
	const navigate = useNavigate();
	const { user, workspace } = useAuthUser();
	const { timeRangeValue, setTimeRangeValue, dateFilter, setDateFilter } =
		useOverviewMetricDatetime({ cacheKey: 'ai_impact_dashboard_global' });
	const [aiChatSortOrder, setAiChatSortOrder] =
		useState<AiChatSortOrder>('newest');
	const [aiChatRatedMessagesSortOrder, setAiChatRatedMessagesSortOrder] =
		useState<AiChatRatedMessagesSortOrder>('newest');
	const [promptsByMemberSortOrder, setPromptsByMemberSortOrder] =
		useState<AiPromptsByMemberSortOrder>('most_active');

	const timeRange: TimeRangeWithAll = (dateFilter || '30d') as TimeRangeWithAll;

	// Use monthly view for "all" time range, daily for others
	const viewOption = timeRange === 'all' ? 'month' : 'day';

	const handleSetDateFilter = useCallback(
		(value: string) => {
			if (isValidTimeRange(value)) {
				setDateFilter(value);
			}
		},
		[setDateFilter]
	);

	const handleDateFilterChange = useCallback(
		(values: string[]) => {
			const [newDateFilter] = values;
			if (newDateFilter) {
				const previousFilter = dateFilter ?? '30d';
				const newFilterLabel =
					DATE_FILTER_OPTIONS.find((item) => item.value === newDateFilter)
						?.label ?? '';

				trackEvent(
					'ai_impact_dashboard/date_filter_changed',
					{
						previous_filter: previousFilter,
						new_filter: newDateFilter,
						filter_label: newFilterLabel,
					},
					user,
					workspace
				);

				handleSetDateFilter(newDateFilter);
				setTimeRangeValue({
					...timeRangeValue,
					timeRange: newDateFilter,
				});
			}
		},
		[
			handleSetDateFilter,
			setTimeRangeValue,
			timeRangeValue,
			dateFilter,
			user,
			workspace,
		]
	);

	const getDateFilterItems = useCallback(async () => DATE_FILTER_OPTIONS, []);

	const selectedDateFilterLabel = useMemo(
		() =>
			DATE_FILTER_OPTIONS.find((item) => item.value === dateFilter)?.label ??
			'Last 30 days',
		[dateFilter]
	);

	const handleAiChatSortChange = useCallback(
		(newSortOrder: AiChatSortOrder) => {
			trackEvent(
				'ai_impact_dashboard/list_sorted',
				{
					widget_type: 'ai_chats',
					previous_sort_order: aiChatSortOrder,
					new_sort_order: newSortOrder,
					current_date_filter: dateFilter ?? '30d',
				},
				user,
				workspace
			);
			setAiChatSortOrder(newSortOrder);
		},
		[aiChatSortOrder, dateFilter, user, workspace]
	);

	const handleAiChatRatedMessagesSortChange = useCallback(
		(newSortOrder: AiChatRatedMessagesSortOrder) => {
			trackEvent(
				'ai_impact_dashboard/list_sorted',
				{
					widget_type: 'rated_messages',
					previous_sort_order: aiChatRatedMessagesSortOrder,
					new_sort_order: newSortOrder,
					current_date_filter: dateFilter ?? '30d',
				},
				user,
				workspace
			);
			setAiChatRatedMessagesSortOrder(newSortOrder);
		},
		[aiChatRatedMessagesSortOrder, dateFilter, user, workspace]
	);

	const handlePromptsByMemberSortChange = useCallback(
		(newSortOrder: AiPromptsByMemberSortOrder) => {
			trackEvent(
				'ai_impact_dashboard/list_sorted',
				{
					widget_type: 'prompts_by_member',
					previous_sort_order: promptsByMemberSortOrder,
					new_sort_order: newSortOrder,
					current_date_filter: dateFilter ?? '30d',
				},
				user,
				workspace
			);
			setPromptsByMemberSortOrder(newSortOrder);
		},
		[promptsByMemberSortOrder, dateFilter, user, workspace]
	);

	const { data: metrics, isPending: isMetricsLoading } = useApiGetAiChatMetrics(
		{
			queryParams: {
				time_range: dateFilter,
			},
		}
	);

	const { data: impact, isPending: isImpactLoading } = useApiGetAiImpactMetrics(
		{
			queryParams: {
				time_range: dateFilter,
			},
		}
	);

	const { data: timeseries, isPending: isTimeseriesLoading } =
		useApiGetAiChatTimeseries(
			{
				queryParams: {
					time_range: dateFilter,
					view_option: viewOption,
					group_by: 'user_id',
				},
			},
			{ placeholderData: keepPreviousData }
		);

	const {
		data: timeSavingsTimeseries,
		isPending: isTimeSavingsTimeseriesLoading,
	} = useApiGetAiTimeSavingsTimeseries(
		{
			queryParams: {
				time_range: dateFilter,
				view_option: viewOption,
			},
		},
		{ placeholderData: keepPreviousData }
	);

	const {
		allItems: promptsByMemberItems,
		hasNextPage: promptsByMemberHasNextPage,
		isFetching: promptsByMemberIsFetching,
		fetchNextPage: promptsByMemberFetchNextPage,
		isLoading: promptsByMemberIsLoading,
		error: promptsByMemberError,
	} = useInfiniteAiPromptsByMember({
		timeRange,
		sortOrder: promptsByMemberSortOrder,
		limit: 10,
	});

	const {
		allItems: ratedMessagesItems,
		hasNextPage: ratedMessagesHasNextPage,
		isFetching: ratedMessagesIsFetching,
		fetchNextPage: ratedMessagesFetchNextPage,
		isLoading: ratedMessagesIsLoading,
		error: ratedMessagesError,
	} = useInfiniteAiChatRatedMessages({
		timeRange,
		sortOrder: aiChatRatedMessagesSortOrder,
		limit: 10,
	});

	const {
		allItems: aiChatListItems,
		hasNextPage: aiChatListHasNextPage,
		isFetching: aiChatListIsFetching,
		fetchNextPage: aiChatListFetchNextPage,
		isLoading: aiChatListIsLoading,
		error: aiChatListError,
	} = useInfiniteAiChatList({
		timeRange,
		sortOrder: aiChatSortOrder,
		limit: 10,
	});

	const handleAiChatClick = useCallback(
		(dataIndex: number) => {
			const point = timeseries?.points?.[dataIndex];
			if (point) {
				trackEvent(
					'ai_impact_dashboard/chart_point_clicked',
					{
						chart_type: 'ai_chat_overview',
						timestamp: point.timestamp,
						current_date_filter: dateFilter ?? '30d',
					},
					user,
					workspace
				);
			}
			navigateToAiChatsWithDateFilter(navigate, dataIndex, timeseries?.points);
		},
		[navigate, timeseries?.points, dateFilter, user, workspace]
	);

	const handleTimeSavingsClick = useCallback(
		(dataIndex: number) => {
			const point = timeSavingsTimeseries?.points?.[dataIndex];
			if (point) {
				trackEvent(
					'ai_impact_dashboard/chart_point_clicked',
					{
						chart_type: 'time_savings',
						timestamp: point.timestamp,
						total_hours_saved: point.total_time_saved_hours.toString(),
						current_date_filter: dateFilter ?? '30d',
					},
					user,
					workspace
				);
			}
			navigateToAiChatsWithDateFilter(
				navigate,
				dataIndex,
				timeSavingsTimeseries?.points
			);
		},
		[navigate, timeSavingsTimeseries?.points, dateFilter, user, workspace]
	);

	const handleSeeAllClick = useCallback(
		(widgetType: 'ai_chats' | 'rated_messages' | 'prompts_by_member') => {
			const visibleItemsCount =
				widgetType === 'ai_chats'
					? aiChatListItems.length
					: widgetType === 'rated_messages'
						? ratedMessagesItems.length
						: promptsByMemberItems.length;

			const currentSortOrder =
				widgetType === 'ai_chats'
					? aiChatSortOrder
					: widgetType === 'rated_messages'
						? aiChatRatedMessagesSortOrder
						: promptsByMemberSortOrder;

			trackEvent(
				'ai_impact_dashboard/view_all_clicked',
				{
					widget_type: widgetType,
					current_sort_order: currentSortOrder,
					current_date_filter: dateFilter ?? '30d',
					visible_items_count: visibleItemsCount.toString(),
				},
				user,
				workspace
			);

			navigateToAiChats(navigate);
		},
		[
			navigate,
			aiChatListItems.length,
			ratedMessagesItems.length,
			promptsByMemberItems.length,
			aiChatSortOrder,
			aiChatRatedMessagesSortOrder,
			promptsByMemberSortOrder,
			dateFilter,
			user,
			workspace,
		]
	);

	const handleMetricsCardClick = useCallback(
		(metricType: 'total_queries' | 'total_time_saved_hours') => {
			const metricValue =
				metricType === 'total_queries'
					? (impact?.total_queries ?? 0)
					: (impact?.total_time_saved_hours ?? 0);

			const percentageChange =
				metricType === 'total_queries'
					? (impact?.total_queries_percentage_change ?? 0)
					: (impact?.total_time_saved_hours_percentage_change ?? 0);

			trackEvent(
				'ai_impact_dashboard/metric_card_clicked',
				{
					metric_type: metricType,
					metric_value: metricValue.toString(),
					percentage_change: percentageChange.toString(),
					current_date_filter: dateFilter ?? '30d',
				},
				user,
				workspace
			);

			navigateToAiChatsWithTimeRange(navigate, dateFilter ?? '30d');
		},
		[navigate, dateFilter, impact, user, workspace]
	);

	if (
		!timeseries ||
		!metrics ||
		!impact ||
		isTimeseriesLoading ||
		isMetricsLoading ||
		isImpactLoading
	) {
		return <AiImpactDashboardSkeleton cards={2} />;
	}

	return (
		<Stack spacing="xl" pt="sm" pb="md">
			<Flex justify="space-between" align="center">
				<Selector
					initialValue={[dateFilter ?? '30d']}
					getItems={getDateFilterItems}
					onChange={handleDateFilterChange}
					selectType={SelectType.Single}
					disableSearch
				>
					<SelectorTarget size="md" variant="default">
						{selectedDateFilterLabel}
					</SelectorTarget>
				</Selector>
			</Flex>
			<AiMetricsCards
				totalQueries={impact.total_queries}
				totalTimeSavedHours={impact.total_time_saved_hours}
				totalQueriesPercentageChange={impact.total_queries_percentage_change}
				totalTimeSavedHoursPercentageChange={
					impact.total_time_saved_hours_percentage_change
				}
				totalQueriesMultiplier={impact.total_queries_multiplier}
				totalTimeSavedHoursMultiplier={impact.total_time_saved_hours_multiplier}
				onTotalQueriesClick={() => handleMetricsCardClick('total_queries')}
				onTotalTimeSavedClick={() =>
					handleMetricsCardClick('total_time_saved_hours')
				}
				dateFilter={dateFilter ?? '30d'}
			/>
			<AiChatOverviewChart
				timeseries={timeseries}
				dateFilter={dateFilter ?? '30d'}
				setDateFilter={handleSetDateFilter}
				setTimeRangeValue={setTimeRangeValue}
				timeRangeValue={timeRangeValue}
				dateRangeOption={viewOption}
				onClick={handleAiChatClick}
			/>
			<AiTimeSavingsOverviewChart
				timeseries={timeSavingsTimeseries}
				dateFilter={dateFilter ?? '30d'}
				setDateFilter={handleSetDateFilter}
				setTimeRangeValue={setTimeRangeValue}
				timeRangeValue={timeRangeValue}
				dateRangeOption={viewOption}
				isLoading={isTimeSavingsTimeseriesLoading}
				onClick={handleTimeSavingsClick}
			/>
			<Group w="100%" spacing="xl" align="flex-start" noWrap>
				<AiPromptsByMemberList
					items={promptsByMemberItems}
					onSortChange={handlePromptsByMemberSortChange}
					sortOrder={promptsByMemberSortOrder}
					isLoading={promptsByMemberIsLoading}
					error={promptsByMemberError}
					handleSeeAllClick={() => handleSeeAllClick('prompts_by_member')}
					hasNextPage={promptsByMemberHasNextPage}
					isFetching={promptsByMemberIsFetching}
					onLoadMore={promptsByMemberFetchNextPage}
					dateFilter={dateFilter ?? '30d'}
				/>
				<AiChatRatedMessages
					items={ratedMessagesItems}
					onSortChange={handleAiChatRatedMessagesSortChange}
					sortOrder={aiChatRatedMessagesSortOrder}
					isLoading={ratedMessagesIsLoading}
					error={ratedMessagesError}
					handleSeeAllClick={() => handleSeeAllClick('rated_messages')}
					hasNextPage={ratedMessagesHasNextPage}
					isFetching={ratedMessagesIsFetching}
					onLoadMore={ratedMessagesFetchNextPage}
					dateFilter={dateFilter ?? '30d'}
				/>
			</Group>
			<AiChatList
				items={aiChatListItems}
				onSortChange={handleAiChatSortChange}
				sortOrder={aiChatSortOrder}
				isLoading={aiChatListIsLoading}
				error={aiChatListError}
				handleSeeAllClick={() => handleSeeAllClick('ai_chats')}
				badgeComponent={({ score }: { score: number }) => (
					<AiQualityBadgeController score={score} />
				)}
				hasNextPage={aiChatListHasNextPage}
				isFetching={aiChatListIsFetching}
				onLoadMore={aiChatListFetchNextPage}
				dateFilter={dateFilter ?? '30d'}
			/>
		</Stack>
	);
});
